import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Member } from '@hero/schema';
import { SharedTable, type StatusBadgeConfigType } from '../../../app/Table/SharedTable';
import type { SearchInputProps } from '../../../app/Search/SearchInput';
import { MemberRow } from './MemberRow';

export interface MemberStatusFilterConfig {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  options: Array<{
    value: string;
    label: string;
    count?: number;
    icon?: React.ReactNode;
    className?: string;
  }>;
  counts?: Array<{ value: string; count: number }>;
}

export interface MemberTableFilters {
  showSearch?: boolean;
  statusFilter?: MemberStatusFilterConfig;
}

export interface MemberTableProps {
  members: Member[];
  isLoading?: boolean;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  searchProps?: SearchInputProps;
  onSort?: (column: string) => void;
  onLoadMore?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  filters?: MemberTableFilters;
  /** Empty-state shown only when the list is empty AND no filters/search are active. */
  emptyState?: React.ReactNode;
  /** Set to `true` whenever the user has search / filter / sort applied. */
  hasActiveFilters?: boolean;
  children?: React.ReactNode;
}

const MEMBER_STATUS_BADGE_CONFIG: StatusBadgeConfigType = {
  getStatusClass: (type: string) => {
    switch (type) {
      case 'admin':
        return 'bg-primary-100 text-logoBlue';
      case 'dispatcher':
      case 'employee':
      case 'driver':
        return 'bg-success-100 text-success';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  },
  getStatusDisplayText: (status: string) => status,
};

const MemberTableComponent: React.FC<MemberTableProps> = ({
  members,
  isLoading,
  sortColumn,
  sortDirection = 'asc',
  searchProps,
  onSort,
  onLoadMore = () => {},
  hasNextPage = false,
  isFetchingNextPage = false,
  filters,
  emptyState,
  hasActiveFilters,
  children,
}) => {
  const { t } = useTranslation(['m-member']);

  const columns = useMemo(
    () => [
      { key: 'name', label: t('m-member:table.columns.name') },
      { key: 'phone', label: t('m-member:table.columns.phone') },
      { key: 'type', label: t('m-member:table.columns.type') },
      { key: 'actions', label: '', sortable: false },
    ],
    [t],
  );

  return (
    <SharedTable
      data={members}
      isLoading={isLoading}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      searchProps={searchProps}
      onSort={onSort}
      statusBadgeConfig={MEMBER_STATUS_BADGE_CONFIG}
      onLoadMore={onLoadMore}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      emptyState={emptyState}
      hasActiveFilters={hasActiveFilters}
      getRowId={(member: Member) => member._id?.toString() || member.userId?.toString() || ''}>
      {children || <MemberTableHead filters={filters} />}

      <SharedTable.Mid>
        <SharedTable.Rows
          columns={columns}
          rowRender={(member: Member) => <MemberRow member={member} />}
        />
      </SharedTable.Mid>
    </SharedTable>
  );
};

const MemberTableHead: React.FC<{ filters?: MemberTableFilters }> = ({ filters }) => {
  if (!filters) return null;

  return (
    <SharedTable.Head>
      {filters.showSearch !== false && <SharedTable.Search />}
      {filters.statusFilter && (
        <SharedTable.Badge
          options={filters.statusFilter.options}
          counts={filters.statusFilter.counts}
          value={filters.statusFilter.value}
          onChange={filters.statusFilter.onChange}
          showCounts={false}
        />
      )}
    </SharedTable.Head>
  );
};

export const MemberTable = Object.assign(MemberTableComponent, {
  Head: SharedTable.Head,
  Search: SharedTable.Search,
  Badge: SharedTable.Badge,
  Mid: SharedTable.Mid,
  Rows: SharedTable.Rows,
});
